import {
  apiGetDebtorInvoices,
  apiGetSignicatStatus,
  apiGetSingleInvoice,
  apiPostAddBeneficialOwners,
  apiPutChoosePaymentMethod,
  apiPutTermsAndConditions,
} from '~/services/api';
import {
  BeneficialOwner,
  OwnerApiInterface,
  SingleInvoice,
} from '~/Interfaces';
import { BankIDStatus } from '~/enums';
import { pushToSentryAction } from '~/store/actions/sentryActions';
import { ApiStatus, AppThunk } from '../types/sharedTypes';
import {
  CHOOSE_PAYMENT,
  CONFIRM_SPLIT_PAYMENT_FAILED,
  CONFIRM_SPLIT_PAYMENT_IDLE,
  CONFIRM_SPLIT_PAYMENT_STARTED,
  CONFIRM_SPLIT_PAYMENT_SUCCESS,
  FETCH_SINGLE_INVOICE,
  InvoiceActon,
  SET_API_DEBTOR_INVOICES_STATUS,
  SET_API_INVOICE_STATUS,
  SET_SIGNICAT_STATUS,
  SET_UBO_API_STATUS,
  UPDATE_DEBTOR_INVOICES,
  UPDATE_UBO,
} from '../types/invoiceTypes';

export const updateApiInvoiceStatus = (status: ApiStatus): InvoiceActon => ({
  type: SET_API_INVOICE_STATUS,
  payload: status,
});

export const updateInvoice = (invoice: SingleInvoice): InvoiceActon => ({
  type: FETCH_SINGLE_INVOICE,
  payload: invoice,
});

export const updateUBO = (data: {
  beneficialOwners: BeneficialOwner[];
  beneficialOwnersChanged: boolean;
}): InvoiceActon => ({
  type: UPDATE_UBO,
  payload: data,
});

export const confirmSplitPaymentStarted = (
  externalId?: string
): InvoiceActon => ({
  type: CONFIRM_SPLIT_PAYMENT_STARTED,
  payload: { externalId },
});
export const confirmSplitPaymentSuccess = (
  externalId?: string
): InvoiceActon => ({
  type: CONFIRM_SPLIT_PAYMENT_SUCCESS,
  payload: { externalId },
});
export const confirmSplitPaymentFailed = (
  externalId?: string
): InvoiceActon => ({
  type: CONFIRM_SPLIT_PAYMENT_FAILED,
  payload: { externalId },
});
export const confirmSplitPaymentIdle = (): InvoiceActon => ({
  type: CONFIRM_SPLIT_PAYMENT_IDLE,
  payload: {},
});
export const updateUboApiStatus = (status: ApiStatus): InvoiceActon => ({
  type: SET_UBO_API_STATUS,
  payload: status,
});
export const choosePayment = (status: ApiStatus): InvoiceActon => ({
  type: CHOOSE_PAYMENT,
  payload: status,
});
export const updateSignicatStatus = (status: ApiStatus): InvoiceActon => ({
  type: SET_SIGNICAT_STATUS,
  payload: status,
});
export const updateDebtorInvoices = (
  invoices: SingleInvoice[],
  concat: boolean,
  count: number
): InvoiceActon => ({
  type: UPDATE_DEBTOR_INVOICES,
  payload: { invoices, concat, count },
});
export const updateApiDebtorInvoicesStatus = (
  status: ApiStatus
): InvoiceActon => ({
  type: SET_API_DEBTOR_INVOICES_STATUS,
  payload: status,
});

export const fetchSingleInvoice = (id: string): AppThunk => (
  dispatch
): void => {
  dispatch(updateApiInvoiceStatus(ApiStatus.Started));
  apiGetSingleInvoice(id)
    .then((res) => {
      dispatch(updateInvoice(res.data));
      dispatch(updateApiInvoiceStatus(ApiStatus.Completed));
    })
    .catch((err) => {
      dispatch(updateApiInvoiceStatus(ApiStatus.Failed));
      dispatch(pushToSentryAction(err, 'apiGetSingleInvoice'));
    });
};

export const confirmingTermsAndConditions = (
  id: string,
  personalNumber: string
): AppThunk => (dispatch): void => {
  dispatch(confirmSplitPaymentStarted(id));
  apiPutTermsAndConditions(id)
    .then((res) => {
      dispatch(confirmSplitPaymentSuccess(id));
    })
    .catch((err) => {
      dispatch(confirmSplitPaymentFailed(id));
      dispatch(pushToSentryAction(err, 'apiAcceptTersmsAndConditions'));
    });
};

export const chooseSplitPaymentMethod = (
  id: string,
  paymentMethod: string,
  personalNumber: string,
  splitParts: number
): AppThunk => (dispatch): void => {
  dispatch(choosePayment(ApiStatus.Started));
  apiPutChoosePaymentMethod(id, paymentMethod, personalNumber, splitParts)
    .then((res) => {
      dispatch(choosePayment(ApiStatus.Completed));
    })
    .catch((err) => {
      dispatch(choosePayment(ApiStatus.Failed));
      dispatch(pushToSentryAction(err, 'apiChoosePaymentMethod'));
    });
};

export const addBeneficialOwners = (
  id: string,
  owners: OwnerApiInterface[]
): AppThunk => (dispatch): void => {
  dispatch(updateUboApiStatus(ApiStatus.Started));
  apiPostAddBeneficialOwners(id, owners)
    .then((res) => {
      dispatch(updateUBO(res.data));
      if (res.data.beneficialOwnersChanged === true) {
        dispatch(updateUboApiStatus(ApiStatus.Failed));
      } else {
        dispatch(updateUboApiStatus(ApiStatus.Completed));
      }
    })
    .catch((err) => {
      dispatch(updateUboApiStatus(ApiStatus.Failed));
      dispatch(pushToSentryAction(err, 'apiChoosePaymentMethod'));
    });
};

export const getSignicatStatus = (
  id: string,
  personalNumber: string
): AppThunk => (dispatch): void => {
  dispatch(updateSignicatStatus(ApiStatus.Started));
  apiGetSignicatStatus(id, personalNumber)
    .then((res) => {
      if (res.data.status === BankIDStatus.completed) {
        dispatch(updateSignicatStatus(ApiStatus.Completed));
      } else if (
        res.data.status === BankIDStatus.timeout ||
        res.data.status === BankIDStatus.error ||
        res.data.status === BankIDStatus.canceled
      ) {
        dispatch(updateSignicatStatus(ApiStatus.Failed));
      }
    })
    .catch((err) => {
      dispatch(updateSignicatStatus(ApiStatus.Failed));
      dispatch(pushToSentryAction(err, 'apiChoosePaymentMethod'));
    });
};

export const getDebtorInvoices = (
  orgNumber: string,
  page: number,
  limit: number,
  status: string,
  concat = false
): AppThunk => (dispatch): void => {
  dispatch(updateApiDebtorInvoicesStatus(ApiStatus.Started));
  apiGetDebtorInvoices(orgNumber, page, limit, status)
    .then((res) => {
      dispatch(
        updateDebtorInvoices(
          res.data.invoices,
          concat,
          +res.data.metadata.total
        )
      );
      dispatch(updateApiDebtorInvoicesStatus(ApiStatus.Completed));
    })
    .catch((err) => {
      dispatch(updateApiDebtorInvoicesStatus(ApiStatus.Failed));
      dispatch(pushToSentryAction(err, 'apiGetDebtorInvoices'));
    });
};
